import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { waLink } from "../lib/whatsapp";
import { useSEO } from "../lib/seo";
import { MessageCircle } from "lucide-react";

const PRODUCT_TYPES = ["Custom T-Shirts","Oversized T-Shirts","Hoodies","Sports Jerseys","Corporate T-Shirts","College T-Shirts","Couple T-Shirts","Family T-Shirts","Bulk Order"];
const PRINTING = ["DTF","Vinyl","Sublimation","Not sure — recommend for me"];

function buildQuoteMessage(form, designId) {
  const lines = ["Hi PrintMyTees, I want a custom quote.", ""];
  if (designId) lines.push(`Design: ${designId}`);
  if (form.name) lines.push(`Name: ${form.name}`);
  if (form.phone) lines.push(`Phone: ${form.phone}`);
  if (form.email) lines.push(`Email: ${form.email}`);
  if (form.product_type) lines.push(`Product: ${form.product_type}`);
  if (form.quantity) lines.push(`Quantity: ${form.quantity}`);
  if (form.size) lines.push(`Size: ${form.size}`);
  if (form.quality) lines.push(`Quality / GSM: ${form.quality}`);
  if (form.color) lines.push(`Colour: ${form.color}`);
  if (form.printing_method) lines.push(`Printing: ${form.printing_method}`);
  if (form.artwork_url) lines.push(`Artwork: ${form.artwork_url}`);
  if (form.instructions) {
    lines.push("");
    lines.push(`Instructions: ${form.instructions}`);
  }
  return lines.join("\n");
}

export default function CustomOrders() {
  const [params] = useSearchParams();
  const designId = params.get("design") || "";
  useSEO({
    title: "Custom Orders & Bulk Quotes — PrintMyTees",
    description: "Send your idea, artwork, logo or design. Individual and bulk custom apparel orders. Corporate, college, sports teams and events.",
  });
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    product_type: PRODUCT_TYPES[0], quantity: "1",
    size: "", quality: "180 GSM", color: "",
    printing_method: PRINTING[0],
    artwork_url: "",
    instructions: designId ? `Please help me customise design ${designId}.` : "",
    design_id: designId,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();

    const phone = form.phone.replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(phone)) {
      return window.alert("Enter a valid 10-digit Indian mobile number.");
    }

    const quantity = Number(form.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      return window.alert("Enter a valid quantity of at least 1.");
    }

    const msg = buildQuoteMessage({ ...form, phone, quantity: String(quantity) }, designId);
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
  };

  const waMsg = designId
    ? `Hi PrintMyTees, I want to customise design ${designId}. Product: ${form.product_type}, Qty: ${form.quantity}.`
    : `Hi PrintMyTees, I want a custom order. Product: ${form.product_type}, Qty: ${form.quantity}.`;

  return (
    <div className="container-pmt py-10 sm:py-12" data-testid="custom-orders-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-5">
          <div className="overline">Custom Orders</div>
          <h1 className="display-h2 mt-2">Have your own design?</h1>
          <p className="mt-4 text-stone-700">
            Send us your idea, artwork, logo, photo or text and we'll help turn it into custom apparel. Individual pieces or bulk orders.
          </p>
          {designId && (
            <div className="mt-5 chip inline-flex" data-testid="custom-design-ref">Design: {designId}</div>
          )}

          <div className="mt-7 grid gap-3 text-sm text-stone-600">
            <div className="card-pmt p-4">
              <div className="font-semibold text-stone-900">Individual or bulk</div>
              <div className="mt-1">For personal orders, teams, college, corporate and events.</div>
            </div>
            <div className="card-pmt p-4">
              <div className="font-semibold text-stone-900">Choose your print</div>
              <div className="mt-1">DTF, Vinyl or Sublimation — or ask us to recommend.</div>
            </div>
            <div className="card-pmt p-4">
              <div className="font-semibold text-stone-900">Artwork help on WhatsApp</div>
              <div className="mt-1">Share your artwork and discuss requirements with our team.</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 card-pmt p-4 sm:p-6" data-testid="custom-form">
          <section>
            <div className="overline">01 · What you need</div>
            <h2 className="text-xl font-bold mt-1">Product requirements</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="label-pmt">Product Type</div>
                <select className="input-pmt" value={form.product_type} onChange={(e) => set("product_type", e.target.value)} data-testid="custom-product-type">
                  {PRODUCT_TYPES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <div className="label-pmt">Quantity</div>
                <input required min="1" step="1" type="number" inputMode="numeric" className="input-pmt" aria-label="Quantity" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="custom-qty" />
              </div>
              <div>
                <div className="label-pmt">Size(s)</div>
                <input className="input-pmt" placeholder="e.g. 3×M, 5×L, 2×XL" aria-label="Size and quantity breakdown" value={form.size} onChange={(e) => set("size", e.target.value)} data-testid="custom-size" />
              </div>
              <div>
                <div className="label-pmt">T-Shirt Quality / GSM</div>
                <select className="input-pmt" value={form.quality} onChange={(e) => set("quality", e.target.value)} data-testid="custom-quality">
                  <option>180 GSM</option>
                  <option>240 GSM</option>
                  <option>260 GSM</option>
                  <option>Other / Not sure</option>
                </select>
              </div>
              <div>
                <div className="label-pmt">Colour</div>
                <input className="input-pmt" placeholder="e.g. Black" value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="custom-color" />
              </div>
              <div>
                <div className="label-pmt">Printing Method</div>
                <select className="input-pmt" value={form.printing_method} onChange={(e) => set("printing_method", e.target.value)} data-testid="custom-printing">
                  {PRINTING.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </section>

          <section className="mt-8 pt-7 border-t border-[#E6DFD5]">
            <div className="overline">02 · Your details</div>
            <h2 className="text-xl font-bold mt-1">How can we reach you?</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="label-pmt">Name <span className="text-red-500">*</span></div>
                <input required autoComplete="name" className="input-pmt" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="custom-name" />
              </div>
              <div>
                <div className="label-pmt">Phone <span className="text-red-500">*</span></div>
                <input required type="tel" inputMode="tel" autoComplete="tel" className="input-pmt" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="custom-phone" />
              </div>
              <div className="sm:col-span-2">
                <div className="label-pmt">Email <span className="text-red-500">*</span></div>
                <input required type="email" autoComplete="email" className="input-pmt" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="custom-email" />
              </div>
            </div>
          </section>

          <section className="mt-8 pt-7 border-t border-[#E6DFD5]">
            <div className="overline">03 · Artwork</div>
            <h2 className="text-xl font-bold mt-1">Send your artwork</h2>
            <p className="mt-2 text-sm text-stone-600">Paste a Google Drive, Dropbox or image link, or send the artwork directly on WhatsApp.</p>
            <p className="mt-1 text-xs text-stone-500">Artwork link is optional — you can send the file directly to us on WhatsApp.</p>
            <div className="mt-4">
              <input className="input-pmt" placeholder="Paste artwork link (optional)" value={form.artwork_url} onChange={(e) => set("artwork_url", e.target.value)} data-testid="custom-artwork" />
              <a href={waLink("Hi PrintMyTees, I want to send my artwork.")} target="_blank" rel="noreferrer" className="btn-whatsapp mt-3 w-full sm:w-auto text-xs" data-testid="custom-wa-artwork">
                <MessageCircle size={14} /> Send Artwork on WhatsApp
              </a>
            </div>
          </section>

          <section className="mt-8 pt-7 border-t border-[#E6DFD5]">
            <div className="overline">04 · Additional details</div>
            <h2 className="text-xl font-bold mt-1">Anything else we should know?</h2>
            <textarea rows={4} className="input-pmt mt-4" placeholder="Tell us about your design, event, delivery needs or other requirements." value={form.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="custom-instructions" />
          </section>

          <div className="mt-8 pt-6 border-t border-[#E6DFD5]">
            <div className="text-sm font-semibold text-stone-900">Your request will open WhatsApp with the details you entered.</div>
            <div className="mt-2 text-xs text-stone-500">After you submit, we’ll review your requirements, discuss artwork and confirm pricing with you.</div>
            <p className="mt-1 text-xs text-stone-500">We'll review your requirements and confirm pricing and next steps with you.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button type="submit" className="btn-primary w-full sm:w-auto justify-center focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2" data-testid="custom-submit">
                <MessageCircle size={14} /> Request a Quote on WhatsApp
              </button>
            </div>
          </div>
        </form>
      </div>

      <section className="mt-12 sm:mt-16 border-t border-[#E6DFD5] pt-10">
        <div className="overline">What happens next?</div>
        <h2 className="display-h3 mt-2">From your idea to your order.</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            ["01", "Send your requirements", "Tell us the product, quantity and printing you need."],
            ["02", "Share your artwork", "Send your design or artwork on WhatsApp."],
            ["03", "Confirm the details", "We'll discuss pricing, artwork and production details."],
            ["04", "Place your order", "Once everything is confirmed, we'll guide you through the next step."],
          ].map(([num, title, text]) => (
            <div key={num} className="card-pmt p-4 sm:p-5">
              <div className="font-mono text-xs text-[#D95328]">{num}</div>
              <div className="font-semibold mt-2">{title}</div>
              <div className="text-sm text-stone-600 mt-1">{text}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}